import React, { useEffect } from 'react'
import Mic from '@mui/icons-material/Mic'
import Stop from '@mui/icons-material/Stop'
import SendRounded from '@mui/icons-material/SendRounded'
import Close from '@mui/icons-material/Close'

import {
  formatRecordingSeconds,
  useChatAudioRecorder,
  type ChatAudioRecorderPhase,
} from 'hooks/useChatAudioRecorder'
import { alertDanger } from 'utils/toast'

import ChatAudioPlayer from './ChatAudioPlayer'

import * as S from './styles'

export type ChatAudioRecorderApi = ReturnType<typeof useChatAudioRecorder>

interface Props {
  disabled?: boolean
  onSend: (file: File) => void | Promise<void>
  /** Compartilhar estado entre preview (fora) e botão (dentro da barra). */
  recorder?: ChatAudioRecorderApi
  part?: 'all' | 'preview' | 'button'
  /** `send` = mesmo tamanho/posição do botão enviar (WhatsApp-style). */
  buttonLayout?: 'toolbar' | 'send'
}

export function shouldShowChatRecordInSendSlot(
  text: string,
  recorder: ChatAudioRecorderApi,
  pendingAttachmentCount = 0
): boolean {
  if (!recorder.isSupported) return false
  if (pendingAttachmentCount > 0) return false
  if (recorder.phase === 'preview' || recorder.phase === 'processing') {
    return false
  }
  if (recorder.phase === 'recording') return true
  return text.trim() === ''
}

function ChatAudioPreview({
  recorder,
  disabled,
  onSend,
}: {
  recorder: ChatAudioRecorderApi
  disabled?: boolean
  onSend: (file: File) => void | Promise<void>
}) {
  if (recorder.phase === 'processing') {
    return (
      <S.AudioPreviewRow>
        <S.AudioProcessingLabel>Preparando áudio…</S.AudioProcessingLabel>
      </S.AudioPreviewRow>
    )
  }

  if (recorder.phase !== 'preview' || !recorder.previewUrl) {
    return null
  }

  const handleSendPreview = () => {
    const file = recorder.consumeRecordedFile()
    if (!file) return
    void onSend(file)
  }

  return (
    <S.AudioPreviewRow>
      <ChatAudioPlayer src={recorder.previewUrl} variant="preview" />
      <S.AudioPreviewActions>
        <S.AudioPreviewActionBtn
          type="button"
          title="Descartar gravação"
          aria-label="Descartar gravação"
          onClick={recorder.discardPreview}
          disabled={disabled}
        >
          <Close fontSize="small" />
        </S.AudioPreviewActionBtn>
        <S.AudioPreviewSendBtn
          type="button"
          title="Enviar áudio"
          aria-label="Enviar áudio"
          onClick={() => void handleSendPreview()}
          disabled={disabled}
        >
          <SendRounded fontSize="small" />
        </S.AudioPreviewSendBtn>
      </S.AudioPreviewActions>
    </S.AudioPreviewRow>
  )
}

function ChatAudioRecordButton({
  recorder,
  disabled,
  layout = 'toolbar',
}: {
  recorder: ChatAudioRecorderApi
  disabled?: boolean
  layout?: 'toolbar' | 'send'
}) {
  const isRecording = recorder.phase === 'recording'
  const buttonDisabled =
    Boolean(disabled) ||
    recorder.phase === 'preview' ||
    recorder.phase === 'processing'

  if (layout === 'send') {
    return (
      <S.ChatButton
        type="button"
        $recording={isRecording}
        title={isRecording ? 'Parar gravação' : 'Gravar áudio'}
        aria-label={isRecording ? 'Parar gravação' : 'Gravar áudio'}
        disabled={buttonDisabled}
        onClick={() => void recorder.toggleRecording()}
      >
        {isRecording ? (
          <Stop fontSize="large" />
        ) : (
          <Mic fontSize="large" />
        )}
      </S.ChatButton>
    )
  }

  return (
    <S.RecordButton
      type="button"
      $recording={isRecording}
      title={
        recorder.phase === 'recording' ? 'Parar gravação' : 'Gravar áudio'
      }
      aria-label={
        recorder.phase === 'recording' ? 'Parar gravação' : 'Gravar áudio'
      }
      disabled={buttonDisabled}
      onClick={() => void recorder.toggleRecording()}
    >
      {isRecording ? (
        <Stop fontSize="medium" />
      ) : (
        <Mic fontSize="medium" />
      )}
      {isRecording ? (
        <S.RecordTimer>
          {formatRecordingSeconds(recorder.seconds)}
        </S.RecordTimer>
      ) : null}
    </S.RecordButton>
  )
}

const ChatAudioControls: React.FC<Props> = ({
  disabled,
  onSend,
  recorder: recorderProp,
  part = 'all',
  buttonLayout = 'toolbar',
}) => {
  const internalRecorder = useChatAudioRecorder({ maxSeconds: 120 })
  const recorder = recorderProp ?? internalRecorder

  useEffect(() => {
    if (recorder.error && part === 'preview') {
      alertDanger(recorder.error)
    }
  }, [recorder.error, part])

  if (!recorder.isSupported) {
    return null
  }

  if (part === 'preview') {
    return (
      <ChatAudioPreview
        recorder={recorder}
        disabled={disabled}
        onSend={onSend}
      />
    )
  }

  if (part === 'button') {
    return (
      <ChatAudioRecordButton
        recorder={recorder}
        disabled={disabled}
        layout={buttonLayout}
      />
    )
  }

  return (
    <S.AudioControlsStack>
      <ChatAudioPreview
        recorder={recorder}
        disabled={disabled}
        onSend={onSend}
      />
      <ChatAudioRecordButton
        recorder={recorder}
        disabled={disabled}
        layout={buttonLayout}
      />
    </S.AudioControlsStack>
  )
}

export { useChatAudioRecorder, type ChatAudioRecorderPhase }
export default ChatAudioControls
